import React, { useCallback, useState } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import styles from '../Clients.module.scss';
import { getRejectedQuestionsByUser, getRejectedQuestionsToForgive, toggleAllRejectedQuestionsForUser, getIsFetchingRejectedQuestions } from '../store';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Icon } from '../../../shared/icon/Icon';
import { CaretDown, CaretSide, WarningSign } from '../../../shared/icons';
import { RejectedQuestion, RejectedQuestionsTab, RejectionLevel } from '../store/types';
import { WizardTab } from '../../../shared/wizard/WizardTab';
import { Toggle } from '../../../shared/toggle';
import { LoadingDots } from '../../../shared/loading-dots/LoadingDots';
import { RejectedQuestionsForUser } from './RejectedQuestionsForUser';

const { red, amber, green, white, yellowGreen } = styles;

interface OpenUserSections {
    userId: number;
    tab: RejectedQuestionsTab;
}

export const RejectedChatBotQuestions: React.FC = () => {
    const dispatch = useAppDispatch();

    const [openUserTiles, setOpenUserTiles] = useState<OpenUserSections[]>([]);

    const rejectedQuestionsForUsers = useAppSelector(getRejectedQuestionsByUser);
    const rejectedQuestionsToForgive = useAppSelector(getRejectedQuestionsToForgive);
    const isFetchingQuestions = useAppSelector(getIsFetchingRejectedQuestions);

    const toggleUserSection = useCallback((toggledUserId: number, userRejectionLevel: RejectionLevel) => {
        const alreadyOpen = openUserTiles.map(({ userId }) => userId).includes(toggledUserId);
        const tab = userRejectionLevel === RejectionLevel.ALL_FORGIVEN ? RejectedQuestionsTab.FORGIVEN_QUESTIONS : RejectedQuestionsTab.CURRENTLY_REJECTED;
        const updatedOpenSections = alreadyOpen ? openUserTiles.filter(({ userId }) => userId !== toggledUserId) : [...openUserTiles, { userId: toggledUserId, tab }];
        setOpenUserTiles(updatedOpenSections);
    }, [openUserTiles]);

    const getSectionOpen = useCallback((userId: number) => openUserTiles.find(openSection => openSection.userId === userId), [openUserTiles]);
    const getSectionOpenIcon = useCallback((openSection: OpenUserSections | undefined) => !isUndefined(openSection) ? CaretDown : CaretSide, []);

    const changeUserTab = useCallback((userId: number, tab: RejectedQuestionsTab) => {
        const updatedOpenSections = openUserTiles.map(section => {
            if (section.userId === userId) {
                section.tab = tab;
            }
            return section;
        });
        setOpenUserTiles(updatedOpenSections);
    }, [openUserTiles]);

    const rejectionLevel = useCallback((userRejectionLevel: RejectionLevel) => {
        switch (userRejectionLevel) {
            case RejectionLevel.BLOCKED:
                return { label: 'Blocked', colour: red };
            case RejectionLevel.HIGH_RISK:
                return { label: 'At Risk', colour: amber };
            case RejectionLevel.LOW_RISK:
                return { label: 'At Risk', colour: yellowGreen };
            default:
                return { label: 'All Forgiven', colour: green };
        }
    }, []);

    const toggleAllForUser = useCallback((userId: number, questionIds: number[]) => dispatch(toggleAllRejectedQuestionsForUser({ userId, questionIds })), [dispatch]);

    const allSelectedForUser = useCallback((userId: number, questionIds: number[]) => {
        const userQuestionsIds = rejectedQuestionsToForgive.find(rejectedForUser => rejectedForUser.userId === userId)?.questionIds;
        return questionIds.every(id => userQuestionsIds?.includes(id));
    }, [rejectedQuestionsToForgive]);

    const questionsForUser = useCallback((rejectedQuestions: RejectedQuestion[]) => {
        const currentlyRejectedQuestions = rejectedQuestions.filter(({ forgivenBy }) => isNull(forgivenBy));
        const forgivenRejectedQuestions = rejectedQuestions.filter(({ forgivenBy }) => !isNull(forgivenBy));
        return { currentlyRejectedQuestions, forgivenRejectedQuestions };
    }, []);

    return (
        <div className={styles.clientRejectedQuestionsWrapper}>
            {isFetchingQuestions ? <LoadingDots /> :
                <div className={styles.scrollableWrapper}>
                    <Scrollable>
                        {rejectedQuestionsForUsers.map(({ username, userId, userRejectionLevel, rejectedQuestions }) => {
                            const openSection = getSectionOpen(userId);
                            const { currentlyRejectedQuestions, forgivenRejectedQuestions } = questionsForUser(rejectedQuestions);
                            const currentTab = openSection?.tab || RejectedQuestionsTab.CURRENTLY_REJECTED;
                            const tabOpen = (tab: RejectedQuestionsTab) => currentTab === tab;
                            const isRejectedTab = currentTab === RejectedQuestionsTab.CURRENTLY_REJECTED;
                            const questions = isRejectedTab ? currentlyRejectedQuestions : forgivenRejectedQuestions;
                            const { label, colour } = rejectionLevel(userRejectionLevel);
                            const rejectedQuestionIds = currentlyRejectedQuestions.map(({ documentCustomQueryId }) => documentCustomQueryId);
                            const allSelected = allSelectedForUser(userId, rejectedQuestionIds);
                            const hasSeriousRejection = !!currentlyRejectedQuestions.find(({ seriousRejection }) => !!seriousRejection);
                            return (
                                <div key={userId} className={styles.userTile}>
                                    <div className={styles.usernameHeader} onClick={() => toggleUserSection(userId, userRejectionLevel)}>
                                        <div className={styles.collapsibleIcon}>
                                            <Icon icon={getSectionOpenIcon(openSection)} fontSize={15} />
                                        </div>
                                        <div className={styles.username}>
                                            {username}
                                            {hasSeriousRejection && <div className={styles.warningIcon}><Icon icon={WarningSign} fontSize={25} color={red} /></div>}
                                        </div>
                                        <div className={styles.status} style={{ border: `solid 2px ${colour}`, color: colour }}>{label}</div>
                                    </div>
                                    {!isUndefined(openSection) &&
                                    <div className={styles.rejectedQuestionsWrapper}>
                                        <div className={styles.wizardTabs}>
                                            <WizardTab label={`${RejectedQuestionsTab.CURRENTLY_REJECTED} (${currentlyRejectedQuestions.length})`} onClick={() => changeUserTab(userId, RejectedQuestionsTab.CURRENTLY_REJECTED)} selected={tabOpen(RejectedQuestionsTab.CURRENTLY_REJECTED)} nonSelectedColour={white} />
                                            <WizardTab label={`${RejectedQuestionsTab.FORGIVEN_QUESTIONS} (${forgivenRejectedQuestions.length})`} onClick={() => changeUserTab(userId, RejectedQuestionsTab.FORGIVEN_QUESTIONS)} selected={tabOpen(RejectedQuestionsTab.FORGIVEN_QUESTIONS)} nonSelectedColour={white} />
                                        </div>
                                        {isRejectedTab && questions.length > 0 && <div className={styles.selectAllWrapper}>
                                            <Toggle checked={allSelected} onChange={() => toggleAllForUser(userId, rejectedQuestionIds)} />
                                            Select All
                                        </div>}
                                        <RejectedQuestionsForUser userId={userId} questions={questions} currentTab={currentTab} />
                                    </div>
                                    }
                                </div>
                            );
                        })}
                    </Scrollable>
                </div>}
        </div>
    );
};
